import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Sparkles, Power } from "lucide-react";
import StorePromoCard from "../../components/promo/StorePromoCard";

const DAYS = [["mon", "Pon"], ["tue", "Uto"], ["wed", "Sre"], ["thu", "Čet"], ["fri", "Pet"], ["sat", "Sub"], ["sun", "Ned"]];
const TYPES = [["PERCENT", "Procenat (%)"], ["FIXED_OFF", "Fiksni popust (RSD)"], ["FIXED_PRICE", "Happy Hour cena (RSD)"]];

export default function HappyHourPage() {
  const { restaurant, role } = useOutletContext();
  const rid = restaurant.id;
  const canManage = ["OWNER", "MANAGER", "PLATFORM_ADMIN"].includes((role || "").toUpperCase());
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/restaurant/${rid}/happy-hours`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get(`/restaurants/${restaurant.slug}/menu`).then(({ data }) => setProducts(data.products || [])).catch(() => {});
    const pre = sessionStorage.getItem("ponesi_hh_prefill");
    if (pre) { sessionStorage.removeItem("ponesi_hh_prefill"); setForm({ days: DAYS.map((d) => d[0]), discount_type: "PERCENT", ...JSON.parse(pre) }); }
    // eslint-disable-next-line
  }, [rid]);

  const blank = { product_id: "", start_time: "14:00", end_time: "17:00", days: DAYS.map((d) => d[0]),
                  discount_type: "PERCENT", discount_value: 20 };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, discount_value: Number(form.discount_value) };
      if (form.id) await api.put(`/restaurant/${rid}/happy-hours/${form.id}`, body);
      else await api.post(`/restaurant/${rid}/happy-hours`, body);
      setForm(null); await load(); toast.success("Happy Hour je sačuvan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
    finally { setBusy(false); }
  };
  const toggle = async (h) => {
    try { await api.put(`/restaurant/${rid}/happy-hours/${h.id}`, { enabled: !h.enabled }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Greška"); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Obrisati Happy Hour za ${h.product_name}?`)) return;
    await api.delete(`/restaurant/${rid}/happy-hours/${h.id}`); await load();
  };

  return (
    <div className="max-w-3xl" data-testid="happy-hour-page">
      <StorePromoCard rid={rid} canManage={canManage} sampleProduct={products[0]} />
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-black text-2xl">Happy Hour</h1>
        {canManage && !form && (
          <button data-testid="hh-new" onClick={() => setForm(blank)}
            className="rounded-xl bg-brand-red hover:bg-brand-redHover text-white px-4 py-3 font-display font-black text-sm flex items-center gap-2">
            <Sparkles size={16} />NOVI HAPPY HOUR
          </button>
        )}
      </div>

      {form && (
        <div data-testid="hh-form" className="bg-white rounded-2xl border p-4 space-y-3 mb-5">
          <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Proizvod</span>
            <select data-testid="hh-product" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base">
              <option value="">Izaberite proizvod</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {RSD(p.price)}</option>)}
            </select></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Od</span>
              <input data-testid="hh-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Do</span>
              <input data-testid="hh-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Dani</p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(([v, l]) => (
                <button key={v} data-testid={`hh-day-${v}`}
                  onClick={() => setForm({ ...form, days: form.days.includes(v) ? form.days.filter((d) => d !== v) : [...form.days, v] })}
                  className={`rounded-lg px-3 py-2 text-sm font-bold border-2 ${form.days.includes(v) ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-slate-200"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Tip popusta</span>
              <select data-testid="hh-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="block"><span className="text-xs font-black uppercase tracking-wider text-slate-400">Vrednost</span>
              <input data-testid="hh-value" type="number" value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                className="mt-1 w-full rounded-xl border-2 border-slate-200 px-3 py-3 text-base" /></label>
          </div>
          <div className="flex gap-2">
            <button data-testid="hh-save" disabled={busy} onClick={save}
              className="flex-1 rounded-xl bg-brand-red hover:bg-brand-redHover text-white py-3.5 font-display font-black disabled:opacity-50">
              {form.id ? "SAČUVAJ" : "AKTIVIRAJ HAPPY HOUR"}
            </button>
            <button onClick={() => setForm(null)} className="px-4 rounded-xl bg-slate-100 font-bold">Otkaži</button>
          </div>
        </div>
      )}

      {rows.length === 0 && !form && <p data-testid="hh-empty" className="text-slate-400 text-sm">Nema podešenih Happy Hour akcija.</p>}
      <div className="space-y-2">
        {rows.map((h) => (
          <div key={h.id} data-testid={`hh-row-${h.id}`} className="bg-white rounded-xl border p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">
                {h.product_name} {h.active_now && <span data-testid={`hh-active-${h.id}`} className="ml-1 text-[10px] font-black uppercase bg-brand-orange/15 text-brand-orange rounded-full px-2 py-0.5">Aktivan</span>}
              </p>
              <p className="text-xs text-slate-500">
                {h.start_time}–{h.end_time} · {(h.days || []).map((d) => (DAYS.find((x) => x[0] === d) || [])[1]).join(", ")} ·{" "}
                {h.discount_type === "PERCENT" ? `-${h.discount_value}%` : h.discount_type === "FIXED_OFF" ? `-${RSD(h.discount_value)}` : RSD(h.discount_value)}
              </p>
              {h.base_price != null && (
                <p className="text-xs font-bold">
                  <span className="line-through text-slate-400 mr-1">{RSD(h.base_price)}</span>
                  <span className="text-brand-green">{RSD(h.final_price)}</span>
                </p>
              )}
              {!h.enabled && <p className="text-xs font-bold text-slate-400">Pauzirano</p>}
            </div>
            {canManage && (
              <>
                <button data-testid={`hh-toggle-${h.id}`} onClick={() => toggle(h)} title="Uključi/pauziraj"
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${h.enabled ? "bg-brand-green/10 text-brand-green" : "bg-slate-100 text-slate-400"}`}><Power size={15} /></button>
                <button data-testid={`hh-edit-${h.id}`} onClick={() => setForm(h)} className="px-3 h-10 rounded-lg bg-slate-100 text-sm font-bold">Izmeni</button>
                <button data-testid={`hh-delete-${h.id}`} onClick={() => remove(h)}
                  className="w-10 h-10 rounded-lg bg-brand-red/10 text-brand-red flex items-center justify-center"><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
